import { Link } from '@inertiajs/react'
import { NotaryPortalLayout } from '@/components/layout/NotaryPortalLayout'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { NotaryDetailed } from '@/types'

type Props = {
  notary: NotaryDetailed
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function SessionModeBadges({ modes }: { modes: string[] }) {
  if (modes.length === 0) return <span className="text-muted-foreground">None</span>
  return (
    <div className="flex flex-wrap gap-1">
      {modes.map((mode) => (
        <Badge key={mode} variant="outline" className="text-[11px] capitalize font-normal">
          {mode.replace(/_/g, ' ')}
        </Badge>
      ))}
    </div>
  )
}

export default function ProfileShow({ notary }: Props) {
  return (
    <NotaryPortalLayout title="Profile">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Profile</h1>
            <p className="text-sm text-muted-foreground">Your notary profile details</p>
          </div>
          <Button size="sm" asChild className="h-8 text-[13px]">
            <Link href="/notary_portal/profile/edit">Edit Profile</Link>
          </Button>
        </div>

        <DetailGrid
          columns={2}
          items={[
            { label: 'Full Name', value: notary.full_name },
            { label: 'Email', value: notary.email_address },
            { label: 'Phone', value: notary.phone || <span className="text-muted-foreground">Not set</span> },
            { label: 'Status', value: (
              <Badge variant="outline" className="text-[11px] capitalize font-normal">
                {notary.status}
              </Badge>
            ) },
          ]}
        />

        <DetailGrid
          columns={2}
          items={[
            { label: 'Commission Number', value: notary.commission_number },
            { label: 'Commission State', value: notary.commission_state },
            { label: 'Commission Expiry', value: formatDate(notary.commission_expiry) },
            { label: 'Session Modes', value: <SessionModeBadges modes={notary.session_modes} /> },
          ]}
        />

        <div className="py-4 border-b border-border/40">
          <dt className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Bio</dt>
          <dd className="text-sm whitespace-pre-wrap">
            {notary.bio || <span className="text-muted-foreground">No bio provided.</span>}
          </dd>
        </div>

        <DetailGrid
          columns={2}
          items={[
            { label: 'Total Appointments', value: notary.appointments_count },
            { label: 'Total Acts', value: notary.acts_count },
            { label: 'Member Since', value: formatDate(notary.created_at) },
          ]}
        />
      </div>
    </NotaryPortalLayout>
  )
}
